import {useMemo, useRef, useState} from 'react'

import {useSaveSamplingPointsPinnedSectionMutation} from './mutations/useSaveSamplingPointsPinnedSectionMutation'
import {useSamplingPointsPinnedSectionQuery} from './queries/useSamplingPointsPinnedSectionQuery'

const PINNED_KEY_PREFIX = 'settings-sampling-points-pinned'
const LESS_USED_KEY_PREFIX = 'settings-sampling-points-less-used'

function parseIds(value: string | undefined): Set<number> {
  if (!value) return new Set<number>()

  try {
    const parsed: unknown = JSON.parse(value)

    if (!Array.isArray(parsed)) return new Set<number>()

    return new Set<number>(
      parsed.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
    )
  } catch {
    return new Set<number>()
  }
}

interface UseManageSamplingPointSectionsResult {
  pinnedSamplingPointIds: Set<number>
  lessUsedSamplingPointIds: Set<number>
  handleTogglePinnedSamplingPoint: (id: number) => void
  handleToggleLessUsedSamplingPoint: (id: number) => void
  isPending: boolean
}

export function useManageSamplingPointSections(
  plantCode?: string
): UseManageSamplingPointSectionsResult {
  const pinnedKeyIdentifier = plantCode ? `${PINNED_KEY_PREFIX}-${plantCode}` : ''
  const lessUsedKeyIdentifier = plantCode ? `${LESS_USED_KEY_PREFIX}-${plantCode}` : ''

  const {data: pinnedData} = useSamplingPointsPinnedSectionQuery(pinnedKeyIdentifier)
  const {data: lessUsedData} = useSamplingPointsPinnedSectionQuery(lessUsedKeyIdentifier)
  const {mutate: savePinnedSection, isPending: isMutationPending} =
    useSaveSamplingPointsPinnedSectionMutation()
  const pendingMutationsCounterRef = useRef(0)
  const isActionLockedRef = useRef(false)
  const [isActionPending, setIsActionPending] = useState(false)

  const pinnedSamplingPointIds = useMemo(() => parseIds(pinnedData?.value), [pinnedData?.value])

  const lessUsedSamplingPointIds = useMemo(
    () => parseIds(lessUsedData?.value),
    [lessUsedData?.value]
  )

  const runSavePinnedSection = (parameters: Parameters<typeof savePinnedSection>[0]) => {
    if (pendingMutationsCounterRef.current === 0) {
      setIsActionPending(true)
    }

    pendingMutationsCounterRef.current += 1

    savePinnedSection(parameters, {
      onSettled: () => {
        pendingMutationsCounterRef.current -= 1

        if (pendingMutationsCounterRef.current === 0) {
          isActionLockedRef.current = false
          setIsActionPending(false)
        }
      }
    })
  }

  const handleTogglePinnedSamplingPoint = (id: number) => {
    if (!plantCode || isActionLockedRef.current) return

    isActionLockedRef.current = true

    const currentPinned = Array.from(pinnedSamplingPointIds)
    const isPinned = pinnedSamplingPointIds.has(id)
    const nextPinned = isPinned
      ? currentPinned.filter((existingId) => existingId !== id)
      : currentPinned.includes(id)
        ? currentPinned
        : [...currentPinned, id]

    runSavePinnedSection({
      keyIdentifier: pinnedKeyIdentifier,
      value: JSON.stringify(nextPinned),
      samplingPointId: id,
      targetSection: isPinned ? 'allSamplingPoints' : 'pinned',
      shouldNotifySuccess: true
    })

    if (lessUsedSamplingPointIds.has(id)) {
      const nextLessUsed = Array.from(lessUsedSamplingPointIds).filter(
        (existingId) => existingId !== id
      )
      runSavePinnedSection({
        keyIdentifier: lessUsedKeyIdentifier,
        value: JSON.stringify(nextLessUsed),
        shouldNotifySuccess: false
      })
    }
  }

  const handleToggleLessUsedSamplingPoint = (id: number) => {
    if (!plantCode || isActionLockedRef.current) return

    isActionLockedRef.current = true

    const currentLessUsed = Array.from(lessUsedSamplingPointIds)
    const isLessUsed = lessUsedSamplingPointIds.has(id)
    const nextLessUsed = isLessUsed
      ? currentLessUsed.filter((existingId) => existingId !== id)
      : currentLessUsed.includes(id)
        ? currentLessUsed
        : [...currentLessUsed, id]

    runSavePinnedSection({
      keyIdentifier: lessUsedKeyIdentifier,
      value: JSON.stringify(nextLessUsed),
      samplingPointId: id,
      targetSection: isLessUsed ? 'allSamplingPoints' : 'lessUsed',
      shouldNotifySuccess: true
    })

    if (pinnedSamplingPointIds.has(id)) {
      const nextPinned = Array.from(pinnedSamplingPointIds).filter(
        (existingId) => existingId !== id
      )
      runSavePinnedSection({
        keyIdentifier: pinnedKeyIdentifier,
        value: JSON.stringify(nextPinned),
        shouldNotifySuccess: false
      })
    }
  }

  const isPending = isMutationPending || isActionPending

  return {
    pinnedSamplingPointIds,
    lessUsedSamplingPointIds,
    handleTogglePinnedSamplingPoint,
    handleToggleLessUsedSamplingPoint,
    isPending
  }
}
